import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { ApiUtils } from '@core/utils/api.utils';
import { AlertToastService, AuthService, ConfigService, WINDOW } from '@services';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable()
export class JWTErrorInterceptor implements HttpInterceptor {
  private readonly tokenErrors = new Set([
    'Token expired',
    'Token incorrect',
    'User is undefined',
  ]);

  private authService: AuthService;
  private configService: ConfigService;

  constructor(
    private injector: Injector,
    private alertToastService: AlertToastService,
    @Inject(WINDOW) private window: Window,
  ) {
    this.authService = this.injector.get(AuthService);
    this.configService = this.injector.get(ConfigService);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isAPI2Url = ApiUtils.isAPI2Url(req.url, this.configService.apiUrl);

    return next.handle(req)
      .pipe(
        map((event: HttpEvent<unknown>) => {
          if (isAPI2Url) {
            if (event instanceof HttpResponse) {
              return this.handleSuccessResponse(event);
            }
          }

          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          if (isAPI2Url) {
            this.handleErrorResponse(error);
          }

          return throwError(() => error);
        }),
      );
  }

  private handleErrorResponse(event: HttpErrorResponse): void {
    if (event.status === 0 && this.window.navigator.onLine) {
      this.alertToastService.pushError('Вибачте, сталася помилка виконання запиту. Для вирішення проблеми зверніться до служби підтримки: support@novaposhta.ua');

      return;
    }

    if (event.status === 500 || event.status === 504) {
      this.alertToastService.pushError('Вибачте, сталася помилка виконання запиту. Для вирішення проблеми зверніться до служби підтримки: support@novaposhta.ua');

      return;
    }

    if (event.status === 401) {
      this.alertToastService.pushError('Сеанс розірвано. Здійсніть повторну авторизацію');
      this.logout();

      return;
    }

    if (event.status === 413) {
      this.alertToastService.pushError('Обсяг ваших даних занадто великий, ми не змогли опрацювати запит. Зменшіть розмір даних для завантаження');

      return;
    }
  }

  private handleSuccessResponse(event: HttpResponse<any>): HttpResponse<any> {
    const body = event.body;

    if (body && typeof body !== 'string' && body.errors && !Array.isArray(body.errors)) {
      const errorsObj = body.errors;
      const errors = Object.keys(errorsObj).map(key => errorsObj[key]);

      event.body.errorsObject = Object.assign({}, body.errors);
      event.body.errors = errors;
    }

    this.handleTokenErrors(event);

    return event;
  }

  private handleTokenErrors(event: HttpResponse<{ errors: string[] }>): void {
    const errors = event?.body?.errors;

    if (!errors) {
      return;
    }

    if (errors.some(error => this.tokenErrors.has(error))) {
      this.logout();
      this.alertToastService.pushError('Сеанс розірвано. Здійсніть повторну авторизацію');
    }
  }

  private logout(): void {
    this.authService.logout();
  }
}
